import type { QueryClient } from '@tanstack/react-query';
import { fetchStaffSession } from '../lib/api-client.js';
import { useStaffSessionStore } from '../stores/staff-session-store.js';

/* Chave de cache da sessão de staff (ADR-0699). */
const STAFF_SESSION_KEY = ['staff', 'session'] as const;
const SESSION_STALE_MS = 5 * 60_000;

/* Resolve a sessão de staff COM CACHE (ADR-0699). Antes o `beforeLoad` fazia um
   `GET /v1/staff/me` BLOQUEANTE a CADA navegação → cada clique no sidebar travava a tela por
   segundos (o path mudava e a UI só respondia após o round-trip). Agora `ensureQueryData` serve
   do cache dentro do `staleTime` (navegação instantânea) e só refetcha após 5min ou invalidação
   (login/logout/401). Sincroniza o store zustand para o shell ler a metadata sem refetch (§4.9 —
   só metadata não-secreta; o token vive no cookie HttpOnly). */
export async function resolveStaffSession(queryClient: QueryClient): Promise<boolean> {
  const session = await queryClient.ensureQueryData({
    queryKey: STAFF_SESSION_KEY,
    queryFn: fetchStaffSession,
    staleTime: SESSION_STALE_MS,
  });
  const store = useStaffSessionStore.getState();
  if (session === null) {
    store.clear();
    return false;
  }
  store.setSession(session);
  return true;
}

/** Descarta o cache da sessão — chamado no login (força /me fresco) e no logout (impede que uma
    sessão cacheada deixe um operador deslogado reentrar no /authenticated). */
export function clearStaffSessionCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: STAFF_SESSION_KEY });
}

/** Seletor reativo da sessão para componentes do shell. */
export function useStaffSession() {
  return useStaffSessionStore((state) => state.session);
}
